import { useState } from 'react';

/**
 * Hook personnalisé pour gérer les alertes modales
 * 
 * Utilisation :
 * const { success, error, warning, info, confirm, AlertComponent } = useAlert();
 * 
 * // Afficher une alerte
 * success('Opération réussie !');
 * error('Une erreur est survenue');
 * 
 * // Confirmation avec callback
 * confirm('Êtes-vous sûr ?', () => {
 *   deleteItem();
 * });
 */
export const useAlert = () => {
  const [alertConfig, setAlertConfig] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    confirmText: 'OK',
    onConfirm: null,
    showCancel: false,
    cancelText: 'Annuler'
  });

  /**
   * Afficher une alerte personnalisée
   */
  const showAlert = (config) => {
    setAlertConfig({
      isOpen: true,
      type: config.type || 'info',
      title: config.title || '',
      message: config.message || '',
      confirmText: config.confirmText || 'OK',
      onConfirm: config.onConfirm || null,
      showCancel: config.showCancel || false,
      cancelText: config.cancelText || 'Annuler'
    });
  };

  /**
   * Fermer l'alerte
   */
  const closeAlert = () => {
    setAlertConfig(prev => ({ ...prev, isOpen: false }));
  };

  /**
   * Raccourcis pour les types courants
   */
  const success = (message, title = 'Succès') => {
    showAlert({ type: 'success', title, message });
  };

  const error = (message, title = 'Erreur') => {
    showAlert({ type: 'error', title, message });
  };

  const warning = (message, title = 'Attention') => {
    showAlert({ type: 'warning', title, message });
  };

  const info = (message, title = 'Information') => {
    showAlert({ type: 'info', title, message });
  };

  /**
   * Confirmation avec callback
   */
  const confirm = (message, onConfirm, title = 'Confirmation') => {
    showAlert({
      type: 'warning',
      title,
      message,
      showCancel: true,
      confirmText: 'Confirmer',
      onConfirm
    });
  };

  return {
    showAlert,
    closeAlert,
    success,
    error,
    warning,
    info,
    confirm,
    alertConfig,
    isOpen: alertConfig.isOpen
  };
};

export default useAlert;